import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Badge, Spinner, 
  Alert, Modal, Form, InputGroup,
  Row, Col  // Added Row and Col here
} from 'react-bootstrap';
import { 
  FaCalendarAlt, FaPlus, FaEdit, 
  FaTrash, FaSearch, FaMoneyBillWave, FaMapMarkerAlt 
} from 'react-icons/fa';
import axios from 'axios';
import { format } from 'date-fns';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState({
    _id: null,
    eventName: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    location: '',
    additionalPrice: 0
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/events`, {
        headers: { 'x-auth-token': token }
      });
      setEvents(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (currentEvent._id) {
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/events/${currentEvent._id}`,
          currentEvent,
          { headers: { 'x-auth-token': token } }
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/events`,
          currentEvent,
          { headers: { 'x-auth-token': token } }
        );
      }
      setShowModal(false);
      fetchEvents();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save event');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`${process.env.REACT_APP_API_URL}/admin/events/${id}`, {
          headers: { 'x-auth-token': token }
        });
        fetchEvents();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete event');
      }
    }
  };

  const filteredEvents = events.filter(event => 
    event.eventName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.eventDate.includes(searchTerm)
  );

  return (
    <div className="events-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <FaCalendarAlt className="me-2" />
          Events Management
        </h2>
        <Button onClick={() => {
          setCurrentEvent({ 
            _id: null, 
            eventName: '',
            eventDate: '',
            startTime: '',
            endTime: '',
            location: '',
            additionalPrice: 0
          });
          setShowModal(true);
        }}>
          <FaPlus className="me-2" />
          Add New Event
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by event name, location or date"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Event Name</th>
              <th>Date</th>
              <th>Time</th>
              <th>Location</th>
              <th>Additional Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map(event => (
              <tr key={event._id}>
                <td>{event.eventName}</td>
                <td>{format(new Date(event.eventDate), 'PPP')}</td>
                <td>{event.startTime} - {event.endTime}</td>
                <td>
                  <FaMapMarkerAlt className="me-2" />
                  {event.location}
                </td>
                <td>
                  <FaMoneyBillWave className="me-2" />
                  ${event.additionalPrice}
                </td>
                <td>
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2"
                    onClick={() => {
                      setCurrentEvent(event);
                      setShowModal(true);
                    }}
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(event._id)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {currentEvent._id ? 'Edit Event' : 'Add New Event'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Event Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={currentEvent.eventName}
                    onChange={(e) => setCurrentEvent({
                      ...currentEvent,
                      eventName: e.target.value
                    })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Event Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={currentEvent.eventDate}
                    onChange={(e) => setCurrentEvent({
                      ...currentEvent,
                      eventDate: e.target.value
                    })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={currentEvent.startTime}
                    onChange={(e) => setCurrentEvent({
                      ...currentEvent,
                      startTime: e.target.value
                    })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    value={currentEvent.endTime}
                    onChange={(e) => setCurrentEvent({
                      ...currentEvent,
                      endTime: e.target.value
                    })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={currentEvent.location}
                    onChange={(e) => setCurrentEvent({
                      ...currentEvent,
                      location: e.target.value
                    })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Additional Price ($)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={currentEvent.additionalPrice}
                    onChange={(e) => setCurrentEvent({
                      ...currentEvent,
                      additionalPrice: parseFloat(e.target.value) || 0
                    })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Events;